import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isAddress } from 'viem'
import { useEIP712Sign } from '@provex/eip712-utils'
import { sacrificeApi } from '../config/sacrifice-api'
import { useToaster } from '../utils/toast'
import { useAuth } from '.'

const REFERRAL_STORAGE_KEY = 'pending-referral'

interface PendingReferral {
  referrerAddress: string
  projectId: string
}

interface ReferralContextValue {
  // Pending referral state
  pendingReferral: PendingReferral | null
  hasPendingReferral: boolean

  // Actions
  acceptReferral: () => Promise<void>
  dismissReferral: () => void

  // Loading state
  isAccepting: boolean
}

const ReferralContext = createContext<ReferralContextValue | null>(null)

export const useReferral = () => {
  const context = useContext(ReferralContext)
  if (!context) {
    throw new Error('useReferral must be used within a ReferralProvider')
  }
  return context
}

const savePendingReferral = (referral: PendingReferral) => {
  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referral))
  } catch (error) {
    console.warn('Failed to save pending referral:', error)
  }
}

const loadPendingReferral = (): PendingReferral | null => {
  try {
    const stored = localStorage.getItem(REFERRAL_STORAGE_KEY)
    if (!stored) return null
    return JSON.parse(stored)
  } catch (error) {
    console.warn('Failed to load pending referral:', error)
    return null
  }
}

const clearPendingReferral = () => {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear pending referral:', error)
  }
}

interface ReferralProviderProps {
  children: React.ReactNode
  projectId: string
}

export const ReferralProvider: React.FC<ReferralProviderProps> = ({ children, projectId }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [pendingReferral, setPendingReferral] = useState<PendingReferral | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)

  const { isSignedIn, recoveredAddress } = useAuth()
  const { signReferralSubmission } = useEIP712Sign()
  const toaster = useToaster()

  // Parse referral from query parameter in hash
  useEffect(() => {
    // Parse query params from location.search (which in HashRouter is the query part of the hash)
    const searchParams = new URLSearchParams(location.search)
    const refParam = searchParams.get('ref')

    if (refParam) {
      // Validate the address
      if (isAddress(refParam)) {
        const referral: PendingReferral = {
          referrerAddress: refParam.toLowerCase(),
          projectId,
        }

        // Save to localStorage
        savePendingReferral(referral)
        setPendingReferral(referral)

        // Remove the ref parameter from URL
        searchParams.delete('ref')
        const newSearch = searchParams.toString()
        const newPath = location.pathname + (newSearch ? `?${newSearch}` : '')
        navigate(newPath, { replace: true })
      } else {
        console.warn('Invalid referrer address in query parameter:', refParam)
      }
    } else {
      // Check if there's a pending referral in localStorage
      const stored = loadPendingReferral()
      if (stored && stored.projectId === projectId) {
        setPendingReferral(stored)
      }
    }
  }, [location.search, location.pathname, navigate, projectId])

  const acceptReferral = useCallback(async () => {
    if (!pendingReferral || !isSignedIn || !recoveredAddress) {
      toaster.error({
        title: 'Authentication Required',
        description: 'Please sign in to accept the referral',
      })
      return
    }

    // Prevent self-referral
    if (recoveredAddress.toLowerCase() === pendingReferral.referrerAddress.toLowerCase()) {
      toaster.error({
        title: 'Invalid Referral',
        description: 'You cannot refer yourself',
      })
      clearPendingReferral()
      setPendingReferral(null)
      return
    }

    setIsAccepting(true)

    try {
      // Sign the referral submission
      const timestamp = BigInt(Date.now())
      const signature = await signReferralSubmission({
        projectId: pendingReferral.projectId,
        referrerAddress: pendingReferral.referrerAddress,
        timestamp,
      })

      if (!signature) {
        throw new Error('Failed to sign referral submission')
      }

      // Submit to API
      const result = await sacrificeApi.referral.submit({
        signature,
        inputs: {
          projectId: pendingReferral.projectId,
          referrerAddress: pendingReferral.referrerAddress,
          timestamp,
        },
        signal: undefined,
      })

      if (result.success) {
        toaster.success({
          title: 'Referral Accepted',
          description: 'Referral accepted successfully!',
        })
        clearPendingReferral()
        setPendingReferral(null)
      } else {
        throw new Error(result.message || 'Failed to accept referral')
      }
    } catch (error) {
      console.error('Error accepting referral:', error)
      toaster.error({
        title: 'Referral Failed',
        description: error instanceof Error ? error.message : 'Failed to accept referral',
      })
    } finally {
      setIsAccepting(false)
    }
  }, [pendingReferral, isSignedIn, recoveredAddress, signReferralSubmission, toaster])

  const dismissReferral = useCallback(() => {
    clearPendingReferral()
    setPendingReferral(null)
  }, [])

  const value = useMemo(
    () => ({
      pendingReferral,
      hasPendingReferral: !!pendingReferral,
      acceptReferral,
      dismissReferral,
      isAccepting,
    }),
    [pendingReferral, acceptReferral, dismissReferral, isAccepting]
  )

  return <ReferralContext.Provider value={value}>{children}</ReferralContext.Provider>
}
